import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { Card } from '@sincronia/shared';
import { CardFace } from './PlayingCard.js';
import { Button } from '../ui.js';
import { useCardDrag } from '../../hooks/useCardDrag.js';
import { emitFast } from '../../net/socket.js';
import { useT } from '../../i18n/index.js';
import { cn } from '../../lib/ui.js';

/**
 * Mão do jogador.
 *
 * Apenas a menor carta é arrastável: ela aparece ampliada, destacada e à
 * frente. As demais ficam menores e sem interação. O servidor valida tudo de
 * novo — a restrição visual é conforto, não segurança.
 */
export function Hand({
  hand,
  playableCardId,
  canPlay,
  pending,
  stageRef,
  dropRef,
  onDragChange,
  onPlay,
}: {
  hand: Card[];
  playableCardId: string | null;
  canPlay: boolean;
  pending: boolean;
  stageRef: RefObject<HTMLElement | null>;
  dropRef: RefObject<HTMLDivElement | null>;
  onDragChange: (state: { active: boolean; overDrop: boolean }) => void;
  onPlay: (cardId: string) => void;
}): ReactNode {
  const t = useT();
  const cardRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(false);

  const playable = hand.find((card) => card.id === playableCardId) ?? hand[0] ?? null;
  // A mão é exibida da MAIOR para a MENOR: as demais cartas em ordem
  // decrescente e, no fim da linha, a menor — a única jogável.
  const rest = (playable ? hand.filter((card) => card.id !== playable.id) : hand)
    .slice()
    .sort((a, b) => b.value - a.value);
  const enabled = canPlay && !pending && Boolean(playable);

  const drag = useCardDrag({
    enabled,
    stageRef,
    dropRef,
    cardRef,
    onStart: (x, y) => emitFast('drag:start', { x, y }),
    onMove: (x, y) => emitFast('drag:move', { x, y }),
    onDrop: () => {
      if (playable) onPlay(playable.id);
      setSelected(false);
    },
    onCancel: () => emitFast('drag:cancel', {}),
  });

  useEffect(() => {
    onDragChange(drag.state);
  }, [drag.state, onDragChange]);

  useEffect(() => {
    if (!enabled) setSelected(false);
  }, [enabled]);

  if (!playable) {
    return (
      <section className="safe-x flex min-h-32 flex-col items-center justify-center gap-2 px-4">
        <p className="text-sm text-night-400">{t('game.emptyHand')}</p>
      </section>
    );
  }

  return (
    <section
      className="safe-x drag-surface relative flex flex-col items-center gap-2 px-3 pt-1"
      data-testid="hand"
    >
      <div className="flex w-full items-end justify-center gap-2">
        {rest.length > 0 ? (
          <ul className="flex flex-wrap items-end justify-end gap-1" data-testid="hand-rest">
            {rest.map((card) => (
              <li key={card.id}>
                <CardFace value={card.value} size="sm" dimmed />
              </li>
            ))}
          </ul>
        ) : null}

        <div
          ref={cardRef}
          role="button"
          tabIndex={0}
          aria-label={t('game.playCard', { value: playable.value })}
          aria-disabled={!enabled}
          data-testid="playable-card"
          data-card-id={playable.id}
          data-card-value={playable.value}
          className={cn(
            'relative z-10 touch-none will-change-transform',
            enabled ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed opacity-70',
            drag.state.active && 'z-30',
            !drag.state.active && 'transition-transform duration-150 hover:-translate-y-1',
          )}
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
          onPointerCancel={drag.onPointerCancel}
          onClick={() => {
            if (drag.movedRef.current || !enabled) return;
            setSelected((value) => !value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (enabled) setSelected((value) => !value);
          }}
        >
          {/* Mesmo tamanho da carta virada que os outros veem no arrasto. */}
          <CardFace
            value={playable.value}
            size="md"
            elevated
            className={cn(
              'ring-2 transition-shadow',
              selected ? 'ring-pulse-400' : 'ring-white/50',
              drag.state.active && 'shadow-[0_24px_48px_-12px_rgba(124,92,255,0.6)]',
            )}
          />
        </div>
      </div>

      <div className="flex min-h-10 flex-col items-center gap-1">
        {selected && enabled ? (
          <Button
            variant="primary"
            className="min-h-10 px-4 py-2 text-sm"
            data-testid="play-card-button"
            onClick={() => {
              onPlay(playable.id);
              setSelected(false);
            }}
          >
            {t('game.confirmPlay')}
          </Button>
        ) : (
          <p className="text-center text-xs text-night-400">
            {enabled ? t('game.dragHint') : t('game.waitingServer')}
          </p>
        )}
        <p className="text-[11px] text-night-500" data-testid="hand-count">
          {hand.length === 1 ? t('game.cardsOne') : t('game.cards', { count: hand.length })}
        </p>
      </div>
    </section>
  );
}
